import { memo, useMemo } from 'react'
import ShapeRenderer from '@/components/app/shapes/shape-renderer'
import { ShapeTextEditor } from '@/components/app/shapes/shape-text-editor'
import { shallowEqualObjects } from '@/hooks/use-shallow-stable-value'
import { defaultShapeTextParagraphs } from '@/lib/shapes/shape-text-frame'
import type { ShapeLayerItemProps } from '../types/shape-layer-item-props'

/**
 * Equal when the shape and handlers are the same objects and the per-shape presentation and text
 * inputs are field-for-field equal. `ShapeManager` rebuilds those two objects for every shape on
 * every render, so comparing their fields is what lets a drag re-render only the dragged shape.
 */
function areShapeLayerItemPropsEqual(prev: ShapeLayerItemProps, next: ShapeLayerItemProps) {
  return (
    prev.shape === next.shape &&
    prev.handlers === next.handlers &&
    shallowEqualObjects(prev.presentation, next.presentation) &&
    (prev.text === next.text ||
      (prev.text !== null && next.text !== null && shallowEqualObjects(prev.text, next.text)))
  )
}

/** One painted shape: its `ShapeRenderer`, plus a `ShapeTextEditor` when it carries text. */
function ShapeLayerItemView({ shape, presentation, text, handlers }: ShapeLayerItemProps) {
  const {
    zIndex,
    scale,
    isSelected,
    selectionHighlightFrozen,
    showPersistentFrozenOutline,
    frozenOutlinePulseKey,
    isAltHovered,
    isPointerInert,
    opacityMultiplier,
    groupHoverFill,
    isResizingThisShape,
    measuredBounds,
    textOnPathPreview,
  } = presentation
  const {
    handleShapeClickInternal,
    handleShapeDoubleClickInternal,
    handleShapeContextMenuInternal,
    handleBoundsChange,
    onTextOnPathLengthChange,
    onTextOnPathStartDistanceChange,
    handleShapeTextParagraphsChange,
    exitShapeTextEdit,
    onShapeTextContextMenu,
    handleShapeTextOverflowChange,
    setChainTextSelection,
    execChainClipboard,
    replaceChainTextSelection,
    extendChainTextSelection,
    onSelectShapeTextChain,
    onShapeTextChainBackspaceAtStart,
    onShapeTextChainArrowLeftAtStart,
    onShapeTextChainArrowRightAtEnd,
    onShapeTextChainOverflowCaret,
  } = handlers

  const groupHoverStyle = useMemo(
    () => (groupHoverFill === undefined ? undefined : { fill: groupHoverFill }),
    [groupHoverFill]
  )

  const isEditingShapeText = text?.isEditing ?? false

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, zIndex }}>
      <ShapeRenderer
        shape={shape}
        isShapeTextEditing={isEditingShapeText}
        // The live draft wins for the shape being edited; every other shape (including
        // this one once the bar closes) renders its own committed config.
        textOnPath={textOnPathPreview ?? shape.textOnPath}
        isTextOnPathEditing={textOnPathPreview != null}
        onTextOnPathLengthChange={onTextOnPathLengthChange}
        onTextOnPathStartDistanceChange={onTextOnPathStartDistanceChange}
        canvasZoom={scale}
        suppressLayoutPixelSnap={isResizingThisShape}
        pathLayoutBounds={
          isResizingThisShape
            ? {
                x: 0,
                y: 0,
                width: shape.width ?? 0,
                height: shape.height ?? 0,
              }
            : measuredBounds
        }
        onClick={handleShapeClickInternal}
        onDoubleClick={handleShapeDoubleClickInternal}
        onContextMenu={handleShapeContextMenuInternal}
        onBoundsChange={handleBoundsChange}
        isInShapeCreationMode={isPointerInert}
        isSelected={isSelected}
        selectionHighlightFrozen={selectionHighlightFrozen}
        showPersistentFrozenOutline={showPersistentFrozenOutline}
        frozenOutlinePulseKey={frozenOutlinePulseKey}
        isAltHovered={isAltHovered}
        opacityMultiplier={opacityMultiplier}
        groupHoverStyle={groupHoverStyle}
      />
      {text && (
        <ShapeTextEditor
          ref={text.editorRef}
          shape={shape}
          paragraphs={shape.paragraphs ?? defaultShapeTextParagraphs()}
          isEditing={isEditingShapeText}
          wrap={text.wrap}
          canvasZoom={scale}
          initialCaretClientPoint={text.caretClientPoint}
          initialCaretOffset={text.caretOffset}
          onParagraphsChange={handleShapeTextParagraphsChange}
          onExitEdit={(_shapeId, opts) => exitShapeTextEdit(opts)}
          onContextMenu={isEditingShapeText ? onShapeTextContextMenu : undefined}
          suppressExitOnBlur={text.suppressExitOnBlur}
          chainFrameIds={text.chainFrameIds}
          onChainSelectionChange={setChainTextSelection}
          onChainClipboard={execChainClipboard}
          chainSelectionRange={text.chainSelectionRange}
          hasChainSelection={text.hasChainSelection}
          onChainSelectionReplace={replaceChainTextSelection}
          onChainSelectionExtend={extendChainTextSelection}
          hasNextInChain={text.hasNextInChain}
          hasPrevInChain={text.hasPrevInChain}
          opacityMultiplier={opacityMultiplier}
          onChainBackspaceAtStart={
            onShapeTextChainBackspaceAtStart
              ? () => onShapeTextChainBackspaceAtStart(shape.id)
              : undefined
          }
          onChainArrowLeftAtStart={
            onShapeTextChainArrowLeftAtStart
              ? () => onShapeTextChainArrowLeftAtStart(shape.id)
              : undefined
          }
          onChainArrowRightAtEnd={
            onShapeTextChainArrowRightAtEnd
              ? () => onShapeTextChainArrowRightAtEnd(shape.id)
              : undefined
          }
          onChainOverflowCaret={
            onShapeTextChainOverflowCaret
              ? (localCaretOffset) => onShapeTextChainOverflowCaret(shape.id, localCaretOffset)
              : undefined
          }
          onOverflowChange={handleShapeTextOverflowChange}
          // Only for a shape that is actually in a chain. A solo frame gets `undefined`
          // and the editor falls back to its own in-frame select-all — which SUPERSEDES
          // WMPCM-9070's "a solo frame must keep the browser's native select-all": the
          // editor now owns that case explicitly rather than leaving it to the browser
          // (WMPCM-9199). The gate itself is unchanged.
          onSelectChain={
            onSelectShapeTextChain && text.isInChain
              ? () => onSelectShapeTextChain(shape.id)
              : undefined
          }
        />
      )}
    </div>
  )
}

export const ShapeLayerItem = memo(ShapeLayerItemView, areShapeLayerItemPropsEqual)
